import * as React from "react";
import { Box } from "@mui/material";
import { keyframes } from "@mui/material/styles";
import designTokens from "../styles/designTokens";

// Smooth rotation
const spin = keyframes`
  to { transform: rotate(360deg); }
`;

interface CenterSpinnerMUIProps {
  radius?: number;
  thickness?: number;
  ariaLabel?: string;
  speedSec?: number;
  color?: string;
  trackColor?: string;
  arcRatio?: number;
}

const centerSpinnerTokens = designTokens.semantic.loading.centerSpinner;

export default function CenterSpinnerMUI({
  radius = centerSpinnerTokens.radius,
  thickness = centerSpinnerTokens.thickness,
  ariaLabel = centerSpinnerTokens.ariaLabel,
  speedSec = centerSpinnerTokens.speedSec,
  color = centerSpinnerTokens.color,
  trackColor = centerSpinnerTokens.trackColor,
  arcRatio = centerSpinnerTokens.arcRatio, // 0..1, controls arc length
}: CenterSpinnerMUIProps) {
  const size = radius * 2;
  const r = radius - thickness / 2; // stroke centered on circle
  const C = 2 * Math.PI * r; // circumference

  const dash = C * arcRatio;
  const gap = C - dash;

  const prefersReducedMotion = React.useMemo(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    []
  );

  return (
    <Box
      role="status"
      aria-busy="true"
      aria-label={ariaLabel}
      sx={{
        position: "fixed",
        top: centerSpinnerTokens.position.top,
        left: centerSpinnerTokens.position.left,
        transform: centerSpinnerTokens.position.transform,
        width: size,
        height: size,
        pointerEvents: "none",
        zIndex: (t) => t.zIndex.modal + 1,
        userSelect: "none",
      }}
    >
      {/* Rotating group */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "grid",
          placeItems: "center",
          transformOrigin: "50% 50%",
          animation: prefersReducedMotion
            ? "none"
            : `${spin} ${speedSec}s linear infinite`,
          willChange: "transform",
        }}
      >
        {/* SVG ring with arc (stroke-dasharray) */}
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          {/* Track */}
          <circle
            cx={radius}
            cy={radius}
            r={r}
            fill="none"
            stroke={trackColor}
            strokeWidth={thickness}
            strokeLinecap="round"
          />
          {/* Arc */}
          <circle
            cx={radius}
            cy={radius}
            r={r}
            fill="none"
            stroke={color}
            strokeWidth={thickness}
            strokeDasharray={`${dash} ${gap}`}
            strokeLinecap="round"
            transform={`rotate(-90 ${radius} ${radius})`}
          />
        </svg>
      </Box>
    </Box>
  );
}
